import { useNotification } from 'hooks'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useStoreQuery } from '../api'
import s from '../partners.modules.scss'
import { Box, CircularProgress } from '@mui/material'
import { Link } from 'components/core-ui/Link'
import { NewStoreModal } from '../Stores/NewStoreModal'
import { InfoItems } from 'components/core-ui/InfoItems'
import ActionComponents from '../components/ActionComponents'
import { formatUIDate } from 'utils/helpers'
import { AssignmentsTable } from '../common/AssignmentsTable'

const Store = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

  const openEditModal = () => setIsEditModalOpen(true)
  const closeEditModal = () => setIsEditModalOpen(false)

  const { id } = useParams<{ id: string }>()

  const { showNotification } = useNotification()

  const { data: store, isLoading, refetch } = useStoreQuery(showNotification, Number(id), false)

  return (
    <div className={s.mainContainer}>
      {isLoading ? (
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      ) : (
        <>
          <div className={s.breadcrumbsContainer}>
            <Link href="/iam/partners" className={s.breadcrumbsLink}>
              External Partners Management
            </Link>
            <span className={s.breadcrumbsSeparator}>|</span>
            <Link href="/iam/externalPartners/stores" className={s.breadcrumbsLink}>
              Stores
            </Link>
            <span className={s.breadcrumbsSeparator}>|</span>
            <span className={s.breadcrumbsCurrent}>{store.name}</span>
          </div>
          <NewStoreModal
            isModalOpen={isEditModalOpen}
            closeModal={closeEditModal}
            refetchStore={refetch}
            editItem={store}
          />
          <Box className={s.flexContainer}>
            <Box className={s.infoItemsContainer}>
              <InfoItems
                itemsPerRow={2}
                itemsSpacing={2}
                items={[
                  { name: store.name, label: 'Name:' },
                  { name: store.type, label: 'Type:' },
                  { name: store.address, label: 'Location:' },
                  { name: store.isEnabled ? 'Enabled' : 'Disabled', label: 'Status:' },
                  ...(store?.externalIdList?.map(externalId => ({
                    name: externalId.value,
                    label: `${externalId.system.name}:`
                  })) || []),
                  ...(store.regionList?.length
                    ? [
                        {
                          name: store.regionList?.map(
                            region => `${region.category} - ${region.name}`
                          ),
                          label: 'Regions:'
                        }
                      ]
                    : [])
                ]}
                title="Store details"
                actionComponents={<ActionComponents openEditModal={openEditModal} />}
              />
            </Box>
            <Box className={s.infoItemsContainer}>
              <InfoItems
                itemsPerRow={2}
                itemsSpacing={2}
                items={[
                  { name: store.createdBy, label: 'Created by:' },
                  { name: formatUIDate(store.createdDate), label: 'Created date:' },
                  { name: store.lastModifiedBy, label: 'Modified by:' },
                  {
                    name: formatUIDate(store.lastModifiedDate),
                    label: 'Modified date:'
                  }
                ]}
                title="Store metadata"
              />
              <InfoItems
                itemsPerRow={2}
                itemsSpacing={2}
                items={[
                  {
                    name: store.legalEntity?.name,
                    label: 'Name:',
                    render: () => (
                      <Link
                        href={`/iam/externalPartners/legalEntities/${store.legalEntity?.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        {store.legalEntity?.name}
                      </Link>
                    )
                  },
                  { name: store.legalEntity?.isEnabled ? 'Enabled' : 'Disabled', label: 'Status:' },
                  { name: store.legalEntity?.code, label: 'CIF:' },
                  { name: store.legalEntity?.country.name, label: 'Country:' }
                ]}
                title="Managing legal entity"
              />
            </Box>
          </Box>
          <Box>
            <AssignmentsTable storeId={store.id} />
          </Box>
        </>
      )}
    </div>
  )
}

export default Store
